import { getInitials } from '../../lib/initials';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePost, updatePost, archivePost, markPostAsViewed, type Post } from '../../services/posts';
import { useAuth } from '../../hooks/useAuth';
import { getErrorMessage } from '../../lib/api';
import { LikeButton } from './LikeButton';
import { CommentList } from './CommentList';

interface PostCardProps {
  post: Post;
}

export function PostCard({ post }: PostCardProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title ?? '');
  const [editContent, setEditContent] = useState(post.content);
  const [error, setError] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const queryClient = useQueryClient();

  const images = post.imageUrls && post.imageUrls.length > 0
    ? post.imageUrls
    : post.imageUrl
      ? [post.imageUrl]
      : [];

  const isOwnPost = user?.id === post.userId && post.userId != null;
  const isAdmin = Boolean(user?.isAdmin);
  const canArchive = isOwnPost || isAdmin;
  const isArchived = Boolean(post.archivedAt);
  const isExpired = post.expiresAt ? new Date(post.expiresAt) <= new Date() : false;
  
  // Mark post as viewed when it's displayed (only if not own post)
  useEffect(() => {
    if (!isOwnPost) {
      markPostAsViewed(post.id).catch(() => {
        // Silently fail - not critical if view tracking fails
      });
    }
  }, [post.id, isOwnPost]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [post.id]);
  
  // Check if within 15-minute edit window
  const createdAt = new Date(post.createdAt);
  const now = new Date();
  const minutesSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60);
  const canEdit = isOwnPost && minutesSinceCreation <= 15;

  const deleteMutation = useMutation({
    mutationFn: () => deletePost(post.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (err) => {
      alert(getErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { title?: string; content: string }) => updatePost(post.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setIsEditing(false);
      setError('');
    },
    onError: (err) => {
      setError(getErrorMessage(err));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archivePost(post.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (err) => {
      alert(getErrorMessage(err));
    },
  });

  const handleDelete = () => {
    if (confirm('Er du sikker på, at du vil slette dette opslag?')) {
      deleteMutation.mutate();
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!post.eventId && !editTitle.trim()) {
      setError('Overskrift er påkrævet for opslag uden begivenhed');
      return;
    }

    if (!editContent.trim()) {
      setError('Opslag skal indeholde tekst');
      return;
    }

    updateMutation.mutate({
      title: editTitle.trim() || undefined,
      content: editContent.trim(),
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Lige nu';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} t`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} d`;
    
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow mb-4">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        {/* Avatar */}
        {post.user?.profileImageUrl ? (
          <img
            src={post.user.profileImageUrl}
            alt={post.user?.name ?? 'Slettet bruger'}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-primary-600 text-sm font-semibold">
              {getInitials(post.user?.name ?? 'S')}
            </span>
          </div>
        )}

        {/* Name + time */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">{post.user?.name ?? 'Slettet bruger'}</span>
          <span className="text-xs text-gray-400 ml-2">{formatTimeAgo(post.createdAt)}</span>
          {isArchived && (
            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Arkiveret</span>
          )}
          {!isArchived && isExpired && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Udløbet</span>
          )}
        </div>

        {/* Actions for own posts / admin */}
        {(isOwnPost || isAdmin) && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {canEdit && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-gray-400 hover:text-primary-600 transition-colors px-2 py-1 rounded hover:bg-gray-100"
              >
                Rediger
              </button>
            )}
            {canArchive && (
              <button
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                className="text-xs text-gray-400 hover:text-amber-600 transition-colors px-2 py-1 rounded hover:bg-amber-50 disabled:opacity-50"
                title={isArchived ? 'Ophæv arkivering' : 'Arkiver opslag'}
              >
                {isArchived ? 'Gendan' : 'Arkiver'}
              </button>
            )}
            {isOwnPost && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
              >
                Slet
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        {!isEditing && post.title && (
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>
        )}

        {isEditing ? (
          <form onSubmit={handleUpdate} className="space-y-2">
            {!post.eventId && (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Overskrift"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                maxLength={120}
              />
            )}
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={3}
              maxLength={5000}
            />
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="px-4 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Gemmer...' : 'Gem'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditTitle(post.title ?? '');
                  setEditContent(post.content);
                  setError('');
                }}
                className="px-4 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Annuller
              </button>
            </div>
          </form>
        ) : (
          <p className="text-gray-900 whitespace-pre-wrap">{post.content}</p>
        )}

        {/* Images slideshow */}
        {images.length > 0 && !isEditing && (
          <div className="mt-3">
            <div className="relative">
              <img
                src={images[activeImageIndex]}
                alt={`Post billede ${activeImageIndex + 1}`}
                className="rounded-lg w-full max-h-[520px] object-cover"
              />

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/45 text-white w-8 h-8 rounded-full hover:bg-black/65"
                    aria-label="Forrige billede"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((prev) => (prev + 1) % images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/45 text-white w-8 h-8 rounded-full hover:bg-black/65"
                    aria-label="Næste billede"
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="flex justify-center gap-2 mt-2">
                {images.map((_, index) => (
                  <button
                    key={`${post.id}-dot-${index}`}
                    type="button"
                    onClick={() => setActiveImageIndex(index)}
                    className={`w-2.5 h-2.5 rounded-full ${index === activeImageIndex ? 'bg-primary-600' : 'bg-gray-300'}`}
                    aria-label={`Vis billede ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* PDF attachment */}
        {post.pdfUrl && !isEditing && (
          <a
            href={post.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors no-underline"
          >
            <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 17.5v-1h7v1h-7zm0-3v-1h7v1h-7zm0-3v-1h3v1h-3z"/>
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">
              {post.pdfName ?? 'Dokument.pdf'}
            </span>
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>

      {/* Footer - Likes and Comments count */}
      <div className="px-4 py-2 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <LikeButton postId={post.id} initialLikeCount={post._count.likes} likedBy={post.likes?.map((l) => l.user) ?? []} />
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-600">
                {post._count.comments > 0 ? post._count.comments : 'Kommenter'}
              </span>
            </button>
          </div>
          {canEdit && !isEditing && (
            <span className="text-xs text-gray-500">
              Kan redigeres i {Math.max(0, Math.ceil(15 - minutesSinceCreation))} min
            </span>
          )}
        </div>
      </div>

      {/* Comments section */}
      {showComments && <CommentList postId={post.id} />}
    </div>
  );
}
